import React from 'react';
import {
  CheckoutStartHandler,
  GooglePayManager,
  UpdateShippingEstimateHandler,
  GooglePayManagerFragment,
  CheckoutSuccessHandler,
  FinalizeCheckoutHandler,
  GooglePayFinalizeActionFragment,
} from './googlePayManager';
import {
  CoreCheckoutUpdateGooglePayShippingEstimate,
  CoreCheckoutFinalizeGooglePayCheckout,
  core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest_ResultConfirmation as ResultConfirmation,
} from '@reverbdotcom/commons/src/gql/graphql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import { withGooglePayCheckoutResult } from './googlePayHelpers';

interface GooglePayCallbacks {
  onPaymentDataChanged: google.payments.api.PaymentDataChangedHandler;
  onPaymentAuthorized: google.payments.api.PaymentAuthorizedHandler;
  onLoadPaymentData: () => any;
}

interface Props {
  startCheckout: CheckoutStartHandler;
  countryCode: string;
  onSuccess: CheckoutSuccessHandler;
  resultConfirmation?: ResultConfirmation;
}

export function useGooglePayCallbacks({
  startCheckout,
  countryCode,
  onSuccess,
  resultConfirmation = ResultConfirmation.SYNC,
}: Props): GooglePayCallbacks {
  const updateShippingEstimate = useUpdateShippingEstimate();
  const finalizeCheckout = useFinalizeCheckout();

  const manager = React.useRef<GooglePayManager>(
    new GooglePayManager({
      startCheckout,
      updateShippingEstimate,
      finalizeCheckout,
      onSuccess,
      countryCode,
      resultConfirmation,
    }),
  );

  React.useEffect(() => {
    manager.current = new GooglePayManager({
      startCheckout,
      updateShippingEstimate,
      finalizeCheckout,
      onSuccess,
      countryCode,
      resultConfirmation,
    });
  }, [
    startCheckout,
    updateShippingEstimate,
    finalizeCheckout,
    onSuccess,
    countryCode,
    resultConfirmation,
  ]);

  return {
    onLoadPaymentData: manager.current.loadPaymentData,
    onPaymentDataChanged: manager.current.changePaymentData,
    onPaymentAuthorized: manager.current.authorizePayment,
  };
}

export function useUpdateShippingEstimate() {
  const [updateShippingEstimateMutation] = useMutation<
    CoreCheckoutUpdateGooglePayShippingEstimate.Mutation,
    CoreCheckoutUpdateGooglePayShippingEstimate.Variables
  >(
    UPDATE_GOOGLE_PAY_SHIPPING_ESTIMATE,
  );

  return React.useCallback<UpdateShippingEstimateHandler>((input) =>
    withGooglePayCheckoutResult(async () => {
      const result = await updateShippingEstimateMutation({ variables: { input } });

      return result.data.updateCheckoutShippingEstimate.checkout;
    }), [updateShippingEstimateMutation]);
}

export function useFinalizeCheckout() {
  const [finalizeCheckoutMutation] = useMutation<
    CoreCheckoutFinalizeGooglePayCheckout.Mutation,
    CoreCheckoutFinalizeGooglePayCheckout.Variables
  >(
    FINALIZE_GOOGLE_PAY_CHECKOUT,
  );

  return React.useCallback<FinalizeCheckoutHandler>((input) =>
    withGooglePayCheckoutResult(async () => {
      const result = await finalizeCheckoutMutation({ variables: { input } });

      return result.data.finalizeDigitalWalletPayment.checkout;
    }), [finalizeCheckoutMutation]);
}

export const UPDATE_GOOGLE_PAY_SHIPPING_ESTIMATE = gql`
  mutation Core_Checkout_UpdateGooglePayShippingEstimate(
    $input: Input_core_apimessages_CheckoutUpdateShippingEstimateRequest
  ) {
    updateCheckoutShippingEstimate(input: $input) {
      checkout {
        _id
        ...GooglePayManagerFields
      }
    }
  }
  ${GooglePayManagerFragment}
`;

export const FINALIZE_GOOGLE_PAY_CHECKOUT = gql`
   mutation Core_Checkout_FinalizeGooglePayCheckout(
    $input: Input_core_apimessages_CheckoutFinalizeDigitalWalletPaymentRequest
  ) {
    finalizeDigitalWalletPayment(input: $input) {
      checkout {
        _id
        ...GooglePayManagerFields
        action {
          _id
          ...GooglePayFinalizeActionFields
        }
      }
    }
  }
  ${GooglePayManagerFragment}
  ${GooglePayFinalizeActionFragment}
`;
